import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faHistory, faTimes, faUndo, faUser, faCalendarAlt, faInfoCircle, faClock, faChevronUp, faChevronDown, faDownload, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeploymentsService, DeploymentHistoryEntry } from '../../services/deployments.service';
import { Model } from '../../services/models.service';
import { AlertService } from '../alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { ButtonComponent } from '../button/button.component';

import { ModalConfirmComponent } from '../modal-confirm/modal-confirm.component';

@Component({
  selector: 'app-modal-deployment-history',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, TranslateModule, ButtonComponent, ModalConfirmComponent],
  templateUrl: './modal-deployment-history.component.html',
  styleUrls: ['./modal-deployment-history.component.scss']
})
export class ModalDeploymentHistoryComponent implements OnChanges {
  @Input() show = false;
  @Input() model: Model | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() reverted = new EventEmitter<void>();

  faHistory = faHistory;
  faTimes = faTimes;
  faUndo = faUndo;
  faUser = faUser;
  faCalendarAlt = faCalendarAlt;
  faInfoCircle = faInfoCircle;
  faClock = faClock;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faDownload = faDownload;
  faCheckCircle = faCheckCircle;

  loading = false;
  history: DeploymentHistoryEntry[] = [];
  selectedDeployment: DeploymentHistoryEntry | null = null;
  showRevertConfirm = false;
  entryToRevert: DeploymentHistoryEntry | null = null;

  constructor(
    private deploymentsService: DeploymentsService,
    private alert: AlertService,
    private translate: TranslateService,
    public dateFormatter: DateFormatterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show']?.currentValue && this.model?.id) {
      this.loadHistory();
    }
  }

  loadHistory() {
    if (!this.model?.id) return;
    this.loading = true;
    this.deploymentsService.getHistory(String(this.model.id)).subscribe({
      next: (data) => {
        this.history = data;
        this.loading = false;
      },
      error: (err) => {
        this.alert.showError(err);
        this.loading = false;
      }
    });
  }

  onClose() {
    this.close.emit();
    this.selectedDeployment = null;
  }

  onRevert(entry: DeploymentHistoryEntry) {
    if (!this.model?.id) return;
    this.entryToRevert = entry;
    this.showRevertConfirm = true;
  }

  onConfirmRevert() {
    if (!this.model?.id || !this.entryToRevert) return;
    
    this.showRevertConfirm = false;
    const entry = this.entryToRevert;
    this.entryToRevert = null;

    this.alert.showSuccess('MODELS.REVERT.START');
    this.deploymentsService.revert(String(this.model.id), entry.id).subscribe({
      next: () => {
        this.alert.showSuccess('MODELS.REVERT.SUCCESS');
        this.reverted.emit();
        this.onClose();
      },
      error: (err) => {
        this.alert.showError(err);
      }
    });
  }

  onDownloadSnapshot(entry: DeploymentHistoryEntry) {
    const data = JSON.stringify(entry.snapshot, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `snapshot-${this.model?.name}-${entry.id}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  toggleDetails(entry: DeploymentHistoryEntry) {
    this.selectedDeployment = this.selectedDeployment?.id === entry.id ? null : entry;
  }

  formatDate(dateStr: string): string {
    return this.dateFormatter.formatDate(dateStr);
  }

  formatTime(dateStr: string): string {
    return this.dateFormatter.formatTime(dateStr);
  }

  isObject(val: any): boolean {
    return typeof val === 'object' && val !== null;
  }

  formatValue(val: any): string {
    if (val === null || val === undefined || val === '') {
      return 'N/A';
    }
    if (Array.isArray(val)) {
      if (val.length === 0) return 'N/A';
      const items = val.map(v =>
        typeof v === 'object' ? (v.description || v.name || JSON.stringify(v)) : String(v)
      );
      if (items.length > 3) {
        const preview = items.slice(0, 3).join(', ');
        return `${preview}  (+${items.length - 3})`;
      }
      return items.join(', ');
    }
    if (this.isObject(val)) {
      return JSON.stringify(val);
    }
    return String(val);
  }

  getArrayCount(val: any): number {
    return Array.isArray(val) ? val.length : 0;
  }
}
